import { useState, useRef } from 'react';

interface BurstWordProps {
  text: string;
  color: string;
  icons: string[]; // remix icon class names
  particleColors: string[];
}

interface Particle {
  id: number;
  icon: string;
  color: string;
  tx: number;
  ty: number;
  rot: number;
  size: number;
}

export default function BurstWord({ text, color, icons, particleColors }: BurstWordProps) {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [shake, setShake] = useState(false);
  const nextId = useRef(0);

  const handleClick = () => {
    const count = 14;
    const newParticles: Particle[] = Array.from({ length: count }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const distance = 90 + Math.random() * 100;
      return {
        id: nextId.current++,
        icon: icons[Math.floor(Math.random() * icons.length)],
        color: particleColors[Math.floor(Math.random() * particleColors.length)],
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance,
        rot: (Math.random() - 0.5) * 720,
        size: 18 + Math.random() * 22,
      };
    });

    setParticles((prev) => [...prev, ...newParticles]);
    setShake(true);
    window.setTimeout(() => setShake(false), 550);

    // Cleanup particles after animation
    const idsToRemove = new Set(newParticles.map((p) => p.id));
    window.setTimeout(() => {
      setParticles((prev) => prev.filter((p) => !idsToRemove.has(p.id)));
    }, 1200);
  };

  return (
    <span
      onClick={handleClick}
      className={`relative inline-block cursor-pointer select-none transition-transform hover:scale-105 ${
        shake ? 'animate-word-shake' : ''
      }`}
      style={{ color }}
    >
      {text}
      {/* Particles layer — anchored to word center */}
      <span className="absolute left-1/2 top-1/2 w-0 h-0 pointer-events-none">
        {particles.map((p) => (
          <i
            key={p.id}
            className={`${p.icon} burst-particle`}
            style={
              {
                color: p.color,
                fontSize: `${p.size}px`,
                ['--tx' as string]: `${p.tx}px`,
                ['--ty' as string]: `${p.ty}px`,
                ['--rot' as string]: `${p.rot}deg`,
              } as React.CSSProperties
            }
          />
        ))}
      </span>
    </span>
  );
}